import dayjs from "dayjs";
import { WithRouterProps } from "next/dist/client/with-router";
import { withRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Button, TitleSubtitle } from "../..";
import { useRegisterContext } from "../../../contexts/RegisterContext";
import { ButtonContainer, ButtonIcon, ButtonLabel, CTABackAndForthContainer, StyledBlueSpan } from "../../../pages/Register/Register.styles";
import { validateBirthDate } from "../../../utils/dates";
import { Tooltip } from "../../Tooltip/Tooltip";
import { DateOfBirthContainer, DateTextField, FieldsContainer } from "./DateOfBirth.styles";

interface DateOfBirthProps extends WithRouterProps {
    onNextButtonClick: Function;
    onPrevButtonClick: Function;
}

const DateOfBirth: React.FC<DateOfBirthProps> = ({
    onNextButtonClick,
    onPrevButtonClick
}) => {
    const { registerInfo, setRegisterInfo } = useRegisterContext();

    const [day, setDay] = useState<number>();
    const [month, setMonth] = useState<number>();
    const [year, setYear] = useState<number>();

    const [isDateValid, setIsDateValid] = useState<boolean>(false);

    useEffect(() => {
        if (!registerInfo.user.dateOfBirth) {
            return
        }

        const registerInfodateOfBirth = new Date(registerInfo.user.dateOfBirth)
        const registerInfoDay = registerInfodateOfBirth.getDate();
        const registerInfoMonth = registerInfodateOfBirth.getMonth() + 1;
        const registerInfoYear = registerInfodateOfBirth.getFullYear();

        setDay(registerInfoDay);
        setMonth(registerInfoMonth);
        setYear(registerInfoYear);

        validateAndSetDate(registerInfoYear, registerInfoMonth, registerInfoDay);
    }, []);

    const validateAndSetDate = (year: number | undefined, month: number | undefined, day: number | undefined) => {
        if (!year || !month || !day) {
            setIsDateValid(false);
            return;
        }

        const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        
        if(validateBirthDate(year, month, day)) {
            setIsDateValid(true);
            setRegisterInfo(prevRegisterInfo => ({
                ...prevRegisterInfo,
                user: {
                    ...prevRegisterInfo.user,
                    dateOfBirth: dayjs(date).toISOString()
                }
            }));
        } else {
            setIsDateValid(false);
            return;
        }
    }

    const onDayChange = (value: number | undefined) => {
        setDay(value);
        validateAndSetDate(year, month, value);
    }

    const onMonthChange = (value: number | undefined) => {
        setMonth(value);
        validateAndSetDate(year, value, day);
    }

    const onYearChange = (value: number | undefined) => {
        setYear(value);
        validateAndSetDate(value, month, day);
    }

    return (
        <DateOfBirthContainer>
            <TitleSubtitle
                data-cy="register-date-of-birth-title"
                size="sm"
                title="My date of birth is..."
                subTitle={<>Please put your correct birthdate. 
                    To book tickets you must be over the age of 18. <Tooltip tooltipName="whatIfImUnderAge"><StyledBlueSpan>What if I'm under 18?</StyledBlueSpan></Tooltip></>} />
            <FieldsContainer>
                <DateTextField
                    data-cy="register-date-of-birth-day"
                    placeholder="DD"
                    value={day}
                    onChange={onDayChange}
                    InputProps={{
                        inputProps: { min: 0 }
                    }}
                />
                <DateTextField
                    data-cy="register-date-of-birth-month"
                    placeholder="MM"
                    value={month}
                    onChange={onMonthChange}
                    InputProps={{
                        inputProps: { min: 0 }
                    }}
                />
                <DateTextField
                    data-cy="register-date-of-birth-year"
                    placeholder="YYYY"
                    value={year}
                    onChange={onYearChange}
                    InputProps={{
                        inputProps: { min: 0 }
                    }}
                />
            </FieldsContainer>
            <CTABackAndForthContainer>
                <Button
                    size="md"
                    variant="tertiary"
                    onClick={() => onPrevButtonClick()}
                    data-cy="button-booking-next"
                    sx={{ padding: 0 }}
                >
                    <ButtonContainer>
                        <ButtonIcon icon="ArrowLeft" />
                        <ButtonLabel>Back</ButtonLabel>
                    </ButtonContainer>
                </Button>
                <Button
                    data-cy="register-date-of-birth-submit"
                    onClick={() => onNextButtonClick()}
                    disabled={!isDateValid}
                > Continue </Button>
            </CTABackAndForthContainer>
        </DateOfBirthContainer>
    );
};

DateOfBirth.displayName = "DateOfBirth";

const ComponentWithRouter = withRouter(DateOfBirth)

export { ComponentWithRouter as DateOfBirth };
